import { useCallback, useRef, useState } from 'react';

type Options = {
  cooldownMs?: number;
  dedupeWindowMs?: number;
  enqueueLatest?: boolean;
  useArgsInSignature?: boolean; // default: false
};

export function useActionGuard(options: Options = {}) {
  const {
    cooldownMs = 700,
    dedupeWindowMs = 500,
    enqueueLatest = true,
    useArgsInSignature = false,
  } = options;

  const inFlightRef = useRef(false);
  const lastCallAtRef = useRef(0);
  const lastSigRef = useRef<string | null>(null);
  const queuedRef = useRef<null | (() => Promise<void>)>(null);

  const [coolingDown, setCoolingDown] = useState(false);

  const safeStringify = (value: unknown) => {
    try {
      const seen = new WeakSet();
      return JSON.stringify(value, (_k, v: any) => {
        if (v && typeof v === 'object') {
          if (v.nativeEvent || v.target) return '[Event]';
          if (typeof Element !== 'undefined' && v instanceof Element)
            return `[Element:${v.tagName}]`;
          if (seen.has(v)) return '[Circular]';
          seen.add(v);
        }
        if (typeof v === 'function') return '[Function]';
        if (typeof v === 'symbol') return '[Symbol]';
        if (v instanceof Date) return v.toISOString();
        return v;
      });
    } catch {
      return '[Unserializable]';
    }
  };

  const makeSig = (key: string, args: unknown[]) =>
    useArgsInSignature && args?.length ? `${key}:${safeStringify(args)}` : key;

  const guard = useCallback(
    <Args extends unknown[]>(
        key: string,
        fn: (...args: Args) => Promise<void> | void
      ) =>
      async (...args: Args) => {
        const now = Date.now();
        const sig = makeSig(key, args);

        // dedupe
        if (
          lastSigRef.current === sig &&
          now - lastCallAtRef.current < dedupeWindowMs
        ) {
          return;
        }

        if (inFlightRef.current) {
          if (enqueueLatest) {
            const captured = args;
            queuedRef.current = async () => {
              await guard(key, fn)(...captured);
            };
          }
          return;
        }

        // cooldown
        if (coolingDown) return;

        inFlightRef.current = true;
        lastSigRef.current = sig;
        lastCallAtRef.current = now;

        try {
          await fn(...args);
        } finally {
          inFlightRef.current = false;
          setCoolingDown(true);
          setTimeout(() => {
            setCoolingDown(false);
            const q = queuedRef.current;
            queuedRef.current = null;
            if (q) q();
          }, cooldownMs);
        }
      },
    [cooldownMs, dedupeWindowMs, coolingDown, enqueueLatest, useArgsInSignature]
  );

  return {
    guard,
    coolingDown,
    isBusy: () => inFlightRef.current || coolingDown,
  };
}
